import {
    Box,
    Button,
    Dialog,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import { useAddPersonageMutation } from 'store/slices/cards/personage/add-personage';
import { CardsPersonageRequest } from 'types/card/card-personage-list';
import { useSphereTitleListQuery } from 'store/slices/cards/sphere/sphere-list';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    openDialog: boolean;
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
}

const AddPersonage: React.FC<Props> = (props: Props) => {
    const { openDialog, setOpenDialog, refetch } = props;
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CardsPersonageRequest>();

    const { data: getSphereList } = useSphereTitleListQuery();

    const [addPersonage, { error, isError, isSuccess, isLoading }] =
        useAddPersonageMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();
    const onSubmit = (values: CardsPersonageRequest) => {
        addPersonage(values);
    };
    const closeModal = () => {
        setOpenDialog(false);
        reset();
    };

    useEffect(() => {
        if (isSuccess) {
            refetch();
            closeModal();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Dialog aria-hidden={false} open={openDialog} onClose={closeModal}>
                <Box
                    display={'flex'}
                    justifyContent={'center'}
                    bgcolor={rgba(colors.primary.main, 0.5)}
                >
                    <DialogTitle textTransform={'uppercase'}>
                        ADD NEW PERSONAGE
                    </DialogTitle>
                    <DialogIconClose handleClose={closeModal} />
                </Box>

                <DialogContent>
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box display={'flex'} pt={pxToRem(15)}>
                            <Controller
                                name="personageTitle"
                                control={control}
                                rules={{
                                    required: 'Personage Title is required',
                                }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Title"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />

                            <FormControl
                                sx={{ ml: pxToRem(10) }}
                                fullWidth
                                error={Boolean(errors.cardSphereId)}
                            >
                                <InputLabel>Sphere</InputLabel>
                                <Controller
                                    name="cardSphereId"
                                    control={control}
                                    rules={{ required: 'Sphere is required' }}
                                    render={({
                                        field: { name, value = '', onChange },
                                    }) => (
                                        <Select
                                            label="Sphere"
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                            labelId="sphere-label"
                                        >
                                            {getSphereList?.map((status) => (
                                                <MenuItem
                                                    key={status.id.toString()}
                                                    value={status.id.toString()}
                                                >
                                                    {status.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.cardSphereId && (
                                    <FormHelperText>
                                        {errors.cardSphereId.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <Controller
                                name="albumColor"
                                control={control}
                                rules={{
                                    required:
                                        'Personage Album Color is required',
                                }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10) }}
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Album Color"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{ width: '50%' }}
                        onClick={() => formRef.current?.requestSubmit()}
                        variant={'contained'}
                        loading={isLoading}
                    >
                        Create Personage
                    </Button>
                    <Button
                        sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                        onClick={() => closeModal()}
                        variant={'contained'}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Personage!'}
            />
        </>
    );
};
export default AddPersonage;
